import type * as React from "react";
import { Link as AriaLink, type LinkProps } from "react-aria-components";
import { icon } from "../icon.css.ts";
import { link, linkContent, text } from "./Link.css.ts";

export type Size = keyof typeof linkContent.classNames.variants.size;
export type Hierarchy = keyof typeof link.classNames.variants.hierarchy;

export type CommonLinkProps = Pick<
  LinkProps,
  "href" | "download" | "onPress" | "target" | "routerOptions" | "rel"
>;

export interface Props extends CommonLinkProps {
  /** Visual style variant. */
  hierarchy?: Hierarchy;
  /** Text and icon size. */
  size?: Size;
  children: string | number | readonly (string | number)[];
  /** Icon displayed before the link text. */
  leadingIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Icon displayed after the link text. */
  trailingIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Ref to the underlying anchor element. */
  ref?: React.Ref<HTMLAnchorElement>;
}

/**
 * A styled anchor link with optional leading and trailing icons.
 */
export const Link: React.FC<Props> = ({
  hierarchy = "primary",
  size = "m",
  children,
  trailingIcon: TrailingIcon,
  leadingIcon: LeadingIcon,
  ref,
  ...props
}) => {
  return (
    <AriaLink {...props} className={link({ hierarchy })} ref={ref}>
      <div className={linkContent({ size })}>
        {LeadingIcon && <LeadingIcon className={icon({ size })} />}
        <span className={text({ size })}>{children}</span>
        {TrailingIcon && <TrailingIcon className={icon({ size })} />}
      </div>
    </AriaLink>
  );
};
